import { Box, Flex, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import ImageUploader from "components/ImageUploader";
import DocumentPreview from "components/ImageUploader/DocumentPreview";
import IconRoundedCamera from "icons/common/rounded-camera.svg";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DocumentWithId } from "types/document";

interface ChatImagePreviewThumbnailsProps {
  documents: DocumentWithId[];
  selectedImageIndex: number;
  setSelectedImageIndex: React.Dispatch<React.SetStateAction<number>>;
  removeById: (id: string) => void;
  onAddMore: (blob: Blob) => void;
  imageLimit: number;
  isDesktop: boolean;
  thumbnailSize: number;
}

export const ChatImagePreviewThumbnails: React.FC<
  ChatImagePreviewThumbnailsProps
> = ({
  documents,
  selectedImageIndex,
  setSelectedImageIndex,
  removeById,
  onAddMore,
  imageLimit,
  isDesktop,
  thumbnailSize,
}) => {
  const { t } = useTranslation("discussions");
  const [, setIsUploading] = useState(false);
  const atLimit = documents.length >= imageLimit;

  const handleRemove = (index: number) => {
    removeById(documents[index].id);
    if (documents.length === 1) {
      setSelectedImageIndex(-1);
    } else if (index === selectedImageIndex) {
      setSelectedImageIndex(prev => (prev === 0 ? 0 : prev - 1));
    } else if (index < selectedImageIndex) {
      setSelectedImageIndex(prev => prev - 1);
    }
  };

  return (
    <Box style={{ flexShrink: 0, padding: "20px 0", overflowX: "auto" }}>
      <Flex
        gap={isDesktop ? 12 : 20}
        wrap="nowrap"
        justify="center"
        align="center"
        px={16}
        style={{ minWidth: "min-content" }}
      >
        {isDesktop && (
          <ImageUploader
            grayScale={false}
            w={thumbnailSize}
            h={thumbnailSize}
            disabled={atLimit}
            onImageUpload={blob => {
              if (blob) onAddMore(blob);
            }}
            setIsUploading={setIsUploading}
            maxFiles={atLimit ? undefined : imageLimit - documents.length}
            style={{
              backgroundColor: "transparent",
              border: "1px dashed #555",
              borderRadius: 6,
            }}
          >
            <Box
              style={{
                width: thumbnailSize,
                height: thumbnailSize,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
              }}
            >
              <IconRoundedCamera
                className={`${atLimit ? "text-gray-light" : "text-gray-medium"} w-8 h-7`}
              />
              {!atLimit && (
                <IconPlus strokeWidth={2} size={18} className="text-green" />
              )}
            </Box>
          </ImageUploader>
        )}

        {documents.map((file, index) => (
          <Box
            key={file.id}
            style={{
              border:
                index === selectedImageIndex
                  ? "2px solid var(--color-white)"
                  : "2px solid transparent",
              borderRadius: 6,
            }}
          >
            <DocumentPreview
              width={thumbnailSize}
              height={thumbnailSize}
              removable
              handleRemoveDocument={() => handleRemove(index)}
              onClick={() => setSelectedImageIndex(index)}
              document={file.blob}
              isPdf={file.blob.type === "application/pdf"}
            />
          </Box>
        ))}
      </Flex>

      {isDesktop && atLimit && (
        <Text c="var(--color-red-dark)" ta="center" size="sm" mt={8} px={16}>
          * {t("maximumImagesPerMessage", { count: imageLimit })}
        </Text>
      )}
    </Box>
  );
};
